import { nonUndefined } from "Utility";
import { TimeAttackTrack } from "model/Match/Set/TimeAttack/Track/TimeAttackTrack";
import { mapTimeAttackRounds } from "./Round/FsTimeAttackRound";
import { Scores } from "model/Scores";
import { getRosterDriverIds, getRosterId, Roster } from "model/Roster/Roster";
import {
  TimeAttackRound,
  getTimeAttackRoundBestTime,
} from "model/Match/Set/TimeAttack/Track/Round/TimeAttackRound";
import { getAdjustedTime } from "model/Match/AdjustedTime";
import {
  TimeAttackScoring,
  calculateTimeAttackTeamPointsScoring,
  calculateTimeAttackScoring,
} from "model/Match/Set/TimeAttack/Track/Scoring/TimeAttackScoring";
import { calculateFinalPositions } from "model/Positions";
import { FsTimeAttackTrack } from "db/model/match/Set/TimeAttack/Track/FsTimeAttackTrack";
import { isTimeAttackScoringPoints } from "model/Match/Set/TimeAttack/Track/Scoring/TimeAttackScoringPoints";
import { RosterScope } from "model/Roster/RosterScope";

const DNF_OFFSET_MS = 22000;

export const mapTimeAttackTracks = (
  fsModels: FsTimeAttackTrack[],
  rosters: Roster[],
  rosterScope: RosterScope,
  scoring: TimeAttackScoring,
  driversPerTeam: number
) => {
  return nonUndefined(
    fsModels.map((t) =>
      mapTimeAttackTrack(t, rosters, rosterScope, scoring, driversPerTeam)
    )
  );
};

const mapTimeAttackTrack = (
  fsModel: FsTimeAttackTrack,
  rosters: Roster[],
  rosterScope: RosterScope,
  scoring: TimeAttackScoring,
  driversPerTeam: number
): TimeAttackTrack | undefined => {
  const rounds = mapTimeAttackRounds(nonUndefined(fsModel.rounds ?? []));
  const scores = calculateTrackScores(
    rounds,
    fsModel.scoreOverrides,
    rosters,
    rosterScope,
    scoring,
    driversPerTeam
  );

  const isComplete = fsModel.overrideComplete || calculateIsComplete(rounds);

  return {
    finalPositions: calculateFinalPositions(isComplete, scores, compareScores),
    isComplete,
    rounds,
    scores,
    trackId: fsModel.trackId,
  };
};

const calculateTrackScores = (
  rounds: TimeAttackRound[],
  scoreOverrides: Scores | undefined,
  rosters: Roster[],
  rosterScope: RosterScope,
  scoring: TimeAttackScoring,
  driversPerTeam: number
) => {
  const scores: Scores = {};
  for (var i = 0; i < rosters.length; i++) {
    const roster = rosters[i];
    const rosterId = getRosterId(roster);
    if (!rosterId) {
      continue;
    }

    const rosterDriverIds = getRosterDriverIds(roster, rosterScope) ?? [];

    const scoreOverride = !!scoreOverrides
      ? scoreOverrides[rosterId]
      : undefined;
    if (scoreOverride !== undefined) {
      scores[rosterId] = scoreOverride;
    } else {
      const score = calculateTimeAttackTrackTeamScore(
        rounds,
        scoring,
        rosterDriverIds,
        driversPerTeam
      );
      if (score !== undefined) {
        scores[rosterId] = score;
      }
    }
  }

  return scores;
};

const calculateTimeAttackTrackTeamScore = (
  rounds: TimeAttackRound[],
  scoring: TimeAttackScoring,
  rosterDriverIds: string[],
  driversPerTeam: number
) => {
  if (rounds.length === 0) {
    return undefined;
  }

  if (isTimeAttackScoringPoints(scoring)) {
    const rosterTimes = nonUndefined(
      rosterDriverIds.map((driverId) => rounds[0].times[driverId])
    );
    if (!rosterTimes.length) {
      return undefined;
    }

    const sortedTimes = Object.values(rounds[0].times).sort((a, b) => a - b);
    return calculateTimeAttackTeamPointsScoring(
      rosterTimes[0],
      sortedTimes,
      scoring
    );
  }

  // // For driver times per track averaged
  let combinedScore = 0;
  for (let i = 0; i < rosterDriverIds.length; i++) {
    const driverId = rosterDriverIds[i];
    const driverTimes = rounds.map((round) => {
      const best = getTimeAttackRoundBestTime(round);
      if (!best) {
        return undefined;
      }

      return getAdjustedTime(round.times[driverId], best + DNF_OFFSET_MS);
    });

    combinedScore += calculateTimeAttackScoring(driverTimes, scoring) ?? 0;
  }

  if (combinedScore === 0) {
    return 0;
  }

  return combinedScore / driversPerTeam;

  // // For combining team times per round
  // const rosterTrackRoundTimes: (AdjustedTime | undefined)[][] = rounds.map(
  //   (round) => {
  //     const best = getTimeAttackRoundBestTime(round);
  //     if (!best) {
  //       return [undefined];
  //     }

  //     return rosterDriverIds.map((driverId) =>
  //       getAdjustedTime(round.times[driverId], best + DNF_OFFSET_MS)
  //     );
  //   }
  // );

  // return calculateTimeAttackScoring(rosterTrackRoundTimes, scoring);
};

const calculateIsComplete = (rounds: TimeAttackRound[]) => {
  if (rounds.length === 0) {
    return false;
  }

  return rounds.every((r) => !!Object.keys(r.times).length);
};

const compareScores = (scores: Scores, a: string, b: string) => {
  return scores[b] - scores[a];
};
